import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@inertiajs/react";
import DashboardLayout from "../../components/DashboardLayout";
import {
  Settings,
  Image as ImageIcon,
  Info,
  Package,
  ShoppingCart,
  Eye,
  ArrowUpRight,
  Activity,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Star,
  Tag,
  Package2,
  BarChart3,
  Calendar,
} from "lucide-react";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  description?: string;
  color: string;
  trend?: "up" | "down" | "neutral";
  percentage?: string;
}

interface QuickActionCardProps {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  color: string;
}

interface User {
  email: string;
}

interface Stats {
  // Estadísticas generales
  total_products: number;
  active_products: number;
  inactive_products: number;
  total_categories: number;
  total_users: number;
  total_orders: number;
  active_slides: number;

  // Estadísticas de stock
  products_with_stock: number;
  products_out_of_stock: number;
  low_stock_products: number;

  // Estadísticas de promociones
  active_promotions: number;
  products_on_promotion: number;
  total_promotions: number;

  // Productos estrella
  featured_products_count: number;
  featured_products_active: number;

  // Estadísticas financieras
  total_revenue: number;
  pending_orders: number;
  completed_orders: number;
}

interface BestSellingProduct {
  name: string;
  image_url: string;
  quantity: number;
}

interface FeaturedProduct {
  name: string;
  image_url: string;
  price: number;
}

interface RecentActivity {
  action: string;
  item: string;
  time: string;
  type: string;
}

interface PageProps {
  auth: { admin: User };
  stats: Stats;
  best_selling_products: BestSellingProduct[];
  recent_featured_products: FeaturedProduct[];
  recent_activity: RecentActivity[];
  store_logo?: string;
  store_name?: string;
}

const StatCard = ({
  title,
  value,
  icon: Icon,
  description,
  color,
  trend,
  percentage,
}: StatCardProps) => (
  <Card className="relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 relative z-10">
      <CardTitle className="text-sm font-semibold text-slate-600 dark:text-slate-300">
        {title}
      </CardTitle>
      <div className="flex items-center gap-2">
        {trend && (
          <>
            {trend === "up" && (
              <TrendingUp className="h-4 w-4 text-emerald-500" />
            )}
            {trend === "down" && (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </>
        )}
        <div className={`p-2 rounded-lg bg-gradient-to-br ${color.replace('text-', 'from-').replace('-500', '-500/20 to-').replace('-500', '-600/20')} shadow-sm`}>
          <Icon className={`h-5 w-5 ${color}`} />
        </div>
      </div>
    </CardHeader>
    <CardContent className="relative z-10">
      <div className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 bg-clip-text text-transparent">{value}</div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">{description}</p>
        {percentage && (
          <span
            className={`text-xs font-semibold px-2 py-1 rounded-full ${
              trend === "up"
                ? "text-emerald-700 bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/30"
                : trend === "down"
                  ? "text-red-700 bg-red-100 dark:text-red-300 dark:bg-red-900/30"
                  : "text-slate-600 bg-slate-100 dark:text-slate-300 dark:bg-slate-700/30"
            }`}
          >
            {percentage}
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

const QuickActionCard = ({
  title,
  description,
  icon: Icon,
  href,
  color,
}: QuickActionCardProps) => (
  <Link href={href} className="block h-full group">
    <Card className="h-full relative overflow-hidden border-0 bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:scale-[1.02]">
      <div className={`absolute inset-0 bg-gradient-to-br ${color.replace('bg-', '').replace('-500', '-500/10')} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      <CardHeader className="flex flex-row items-center gap-4 relative z-10">
        <div className={`p-3 rounded-xl ${color} shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <CardTitle className="text-lg font-bold text-slate-800 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-slate-100 transition-colors">{title}</CardTitle>
          <CardDescription className="text-slate-600 dark:text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300 transition-colors">{description}</CardDescription>
        </div>
      </CardHeader>
    </Card>
  </Link>
);

const AdminDashboard = ({
  stats,
  best_selling_products,
  recent_featured_products,
  recent_activity,
  store_logo,
  store_name,
}: PageProps) => {
  // Estadísticas principales
  const mainStatCards = [
    {
      title: "Ingresos Totales",
      value: `S/. ${stats.total_revenue.toLocaleString()}`,
      icon: DollarSign,
      description: `${stats.completed_orders} pedidos completados`,
      color: "text-green-500",
      trend: "up" as const,
      percentage: "+12.5%",
    },
    {
      title: "Productos Activos",
      value: stats.active_products,
      icon: Package,
      description: `${stats.total_products} productos en total`,
      color: "text-blue-500",
      trend: "up" as const,
      percentage: `${Math.round((stats.active_products / stats.total_products) * 100)}%`,
    },
    {
      title: "Productos Estrella",
      value: stats.featured_products_active,
      icon: Star,
      description: `${stats.featured_products_count} productos destacados`,
      color: "text-yellow-500",
      trend: "neutral" as const,
    },
    {
      title: "Productos en Oferta",
      value: stats.products_on_promotion,
      icon: Tag,
      description: `${stats.active_promotions} promociones activas`,
      color: "text-purple-500",
      trend: "up" as const,
      percentage: "+8.2%",
    },
  ];

  // Estadísticas secundarias
  const secondaryStatCards = [
    {
      title: "Pedidos Pendientes",
      value: stats.pending_orders,
      icon: Calendar,
      description: "Requieren atención",
      color: "text-orange-500",
      trend: stats.pending_orders > 5 ? ("up" as const) : ("neutral" as const),
    },
    {
      title: "Stock Bajo",
      value: stats.low_stock_products,
      icon: AlertTriangle,
      description: "Productos con ≤5 unidades",
      color: "text-red-500",
      trend:
        stats.low_stock_products > 0 ? ("up" as const) : ("neutral" as const),
    },
    {
      title: "Sin Stock",
      value: stats.products_out_of_stock,
      icon: Package2,
      description: "Productos agotados",
      color: "text-gray-500",
      trend:
        stats.products_out_of_stock > 0
          ? ("down" as const)
          : ("neutral" as const),
    },
    {
      title: "Categorías",
      value: stats.total_categories,
      icon: BarChart3,
      description: "Total de categorías",
      color: "text-indigo-500",
      trend: "neutral" as const,
    },
  ];

  const quickActions = [
    {
      title: "Gestionar Productos",
      description: "Editar, agregar o eliminar productos",
      icon: Package,
      href: "/admin/products",
      color: "bg-blue-500",
    },
    {
      title: "Crear Promoción",
      description: "Configurar nuevas ofertas",
      icon: Tag,
      href: "/admin/promotions",
      color: "bg-purple-500",
    },
    {
      title: "Productos Estrella",
      description: "Gestionar productos destacados",
      icon: Star,
      href: "/admin/products?filter=featured",
      color: "bg-yellow-500",
    },
    {
      title: "Configuración",
      description: "Configuración de la tienda",
      icon: Settings,
      href: "/admin/store_settings",
      color: "bg-gray-500",
    },
    {
      title: "Editar Acerca de",
      description: "Configurar información de la empresa",
      icon: Info,
      href: "/admin/about",
      color: "bg-indigo-500",
    },
    {
      title: "Slides Promocionales",
      description: "Gestionar hero banner",
      icon: ImageIcon,
      href: "/admin/hero_slides",
      color: "bg-green-500",
    },
    {
      title: "Ver Tienda",
      description: "Vista de cliente",
      icon: Eye,
      href: "/",
      color: "bg-orange-500",
    },
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "product":
        return Package;
      case "promotion":
        return Tag;
      case "featured":
        return Star;
      default:
        return Activity;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "product":
        return "text-blue-500 bg-blue-100";
      case "promotion":
        return "text-purple-500 bg-purple-100";
      case "featured":
        return "text-yellow-500 bg-yellow-100";
      default:
        return "text-gray-500 bg-gray-100";
    }
  };

  return (
    <DashboardLayout title="Panel de Administración" storeLogo={store_logo} storeName={store_name}>
      {/* Header */}
      <div className="relative mb-8 p-8 rounded-2xl bg-gradient-to-r from-slate-400 via-slate-500 to-slate-600 shadow-xl overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-300/20 via-slate-400/20 to-slate-500/20 animate-pulse" />
        <div className="absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-xl" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl" />
        <div className="relative z-10">
          <h1 className="text-4xl font-bold text-white mb-2">
            Dashboard Ejecutivo
          </h1>
          <p className="text-slate-50 text-lg">
            Resumen completo de tu tienda y estadísticas en tiempo real.
          </p>
        </div>
      </div>

      {/* Estadísticas Principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {mainStatCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Estadísticas Secundarias */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {secondaryStatCards.map((stat, index) => (
          <StatCard key={index} {...stat} />
        ))}
      </div>

      {/* Grid Principal de Contenido */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Columna Izquierda */}
        <div className="lg:col-span-2 space-y-8">
          {/* Acciones Rápidas */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg">
                  <Activity className="h-6 w-6 text-white" />
                </div>
                Acciones Rápidas
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">
                Gestiona los aspectos más importantes de tu tienda
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quickActions.map((action, index) => (
                <QuickActionCard key={index} {...action} />
              ))}
            </CardContent>
          </Card>

          {/* Actividad Reciente */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 shadow-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
                Actividad Reciente
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">
                Últimas acciones realizadas en tu tienda.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recent_activity.length > 0 ? (
                  recent_activity.map((activity, index) => {
                    const Icon = getActivityIcon(activity.type);
                    return (
                      <div
                        key={index}
                        className="flex items-center space-x-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 dark:hover:from-slate-800 dark:hover:to-slate-700 transition-all duration-200 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-md"
                      >
                        <div
                          className={`p-3 rounded-xl ${getActivityColor(activity.type)} shadow-sm`}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                            {activity.action}
                          </p>
                          <p className="text-sm text-slate-600 dark:text-slate-400">
                            {activity.item}
                          </p>
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded-full">
                          {activity.time}
                        </p>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 inline-block mb-4">
                      <Activity className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">
                      No hay actividad reciente
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Resumen de Stock */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 shadow-lg">
                  <Package2 className="h-6 w-6 text-white" />
                </div>
                Estado del Inventario
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">
                Resumen del estado actual del stock
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-3xl font-bold text-emerald-700 dark:text-emerald-300 mb-2">
                    {stats.products_with_stock}
                  </div>
                  <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">Con Stock</p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-3xl font-bold text-yellow-700 dark:text-yellow-300 mb-2">
                    {stats.low_stock_products}
                  </div>
                  <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">Stock Bajo</p>
                </div>
                <div className="text-center p-6 bg-gradient-to-br from-red-50 to-rose-100 dark:from-red-900/20 dark:to-rose-900/20 rounded-xl border border-red-200 dark:border-red-800 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-3xl font-bold text-red-700 dark:text-red-300 mb-2">
                    {stats.products_out_of_stock}
                  </div>
                  <p className="text-sm font-semibold text-red-600 dark:text-red-400">Sin Stock</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Columna Derecha */}
        <div className="lg:col-span-1 space-y-8">
          {/* Productos Más Vendidos */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                Productos Más Vendidos
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">Top 5 productos este período</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {best_selling_products.length > 0 ? (
                  best_selling_products.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-cyan-50 dark:hover:from-blue-900/20 dark:hover:to-cyan-900/20 transition-all duration-200 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md group"
                    >
                      <div className="relative">
                        <img
                          src={product.image_url || "https://picsum.photos/150"}
                          alt={product.name}
                          className="w-14 h-14 rounded-xl object-cover shadow-sm group-hover:shadow-md transition-shadow"
                        />
                        <span className="absolute -top-2 -right-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
                          {index + 1}
                        </span>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate group-hover:text-blue-700 dark:group-hover:text-blue-300 transition-colors">
                          {product.name}
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                          {product.quantity} vendidos
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" className="hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                        <ArrowUpRight className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 inline-block mb-4">
                      <ShoppingCart className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">
                      No hay ventas registradas
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Productos Estrella Recientes */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-600 shadow-lg">
                  <Star className="h-6 w-6 text-white" />
                </div>
                Productos Destacados
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">
                {stats.featured_products_active} productos estrella activos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recent_featured_products.length > 0 ? (
                  recent_featured_products.map((product, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-4 p-4 rounded-xl hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 dark:hover:from-yellow-900/20 dark:hover:to-orange-900/20 transition-all duration-200 border-l-4 border-gradient-to-b from-yellow-400 to-orange-500 bg-gradient-to-r from-yellow-50/50 to-orange-50/50 dark:from-yellow-900/10 dark:to-orange-900/10 hover:shadow-md group"
                    >
                      <img
                        src={product.image_url || "https://picsum.photos/150"}
                        alt={product.name}
                        className="w-12 h-12 rounded-xl object-cover shadow-sm group-hover:shadow-md transition-shadow"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate group-hover:text-yellow-700 dark:group-hover:text-yellow-300 transition-colors">
                          {product.name}
                        </p>
                        <p className="text-xs text-slate-600 dark:text-slate-400 font-medium">
                          S/. {product.price.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-2 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 shadow-sm">
                        <Star className="h-4 w-4 text-white fill-current" />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="p-4 rounded-full bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600 inline-block mb-4">
                      <Star className="h-12 w-12 text-slate-400 dark:text-slate-500" />
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 font-medium">
                      No hay productos destacados
                    </p>
                  </div>
                )}
              </div>
              <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Link href="/admin/products?filter=featured">
                  <Button variant="outline" className="w-full hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 dark:hover:from-yellow-900/20 dark:hover:to-orange-900/20 border-yellow-300 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 hover:border-yellow-400 dark:hover:border-yellow-500 transition-all duration-200">
                    Ver Todos los Destacados
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Resumen de Promociones */}
          <Card className="border-0 shadow-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-900 dark:to-slate-800">
            <CardHeader className="pb-6">
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-600 shadow-lg">
                  <Tag className="h-6 w-6 text-white" />
                </div>
                Promociones Activas
              </CardTitle>
              <CardDescription className="text-base text-slate-600 dark:text-slate-400">
                {stats.active_promotions} promociones en curso
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-100 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-200 dark:border-purple-800 shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-3xl font-bold text-purple-700 dark:text-purple-300 mb-2">
                    {stats.products_on_promotion}
                  </div>
                  <p className="text-sm font-semibold text-purple-600 dark:text-purple-400">Productos en oferta</p>
                </div>
                <Link href="/admin/promotions">
                  <Button variant="outline" className="w-full hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/20 dark:hover:to-pink-900/20 border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:border-purple-400 dark:hover:border-purple-500 transition-all duration-200">
                    Gestionar Promociones
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Force rebuild
export default AdminDashboard;
